import { useCallback, useEffect, useState } from "react";
import api, { apiErr, downloadFile, photoUrl } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ProvenanceList } from "@/components/ProvenancePanel";
import {
  Download, CheckCircle2, Lock, Database, ChevronDown, Leaf, PenLine, Building2,
  MapPinned, HardHat, Camera, Plus, Trash2, Calendar,
} from "lucide-react";

const BAND_TONE = {
  good: "bg-emerald-100 text-emerald-900 border-emerald-300",
  warn: "bg-amber-100 text-amber-900 border-amber-300",
  bad: "bg-rose-100 text-rose-900 border-rose-300",
};
const scoreColor = (s) =>
  s == null ? "text-slate-400" : s >= 7.5 ? "text-forest-mid" : s >= 6 ? "text-amber-600" : "text-rose-600";

function SiteRecords({ periodId, base, t, k, canEdit, onChange }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [score, setScore] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const records = [...(k.records || [])].sort((a, b) =>
    (b.date || "").localeCompare(a.date || "") || (b.created_at || "").localeCompare(a.created_at || ""));

  const pick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setUploading(true);
    for (const f of picked) {
      try {
        const dataUrl = await compressImage(f);
        const r = await api.post("/photos", { data_url: dataUrl, caption: k.name });
        setFiles((prev) => [...prev, r.data]);
      } catch (err) { toast.error(apiErr(err)); }
    }
    setUploading(false);
  };
  const add = async () => {
    if (score === "") { toast.error("Enter a 0–10 score for this site check"); return; }
    setBusy(true);
    try {
      await api.post(`/score/periods/${periodId}/${base}/kpi/${k.key}/records`, {
        date, score: Number(score), note, photos: files,
      });
      toast.success("Site check recorded");
      setScore(""); setNote(""); setFiles([]); setDate(today);
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const del = async (rid) => {
    try { await api.delete(`/score/periods/${periodId}/${base}/kpi/${k.key}/records/${rid}`); await onChange(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-2">
      {records.length > 0 ? (
        <div className="space-y-1.5">
          {records.map((r, idx) => (
            <div key={r.id} className="rounded-md border border-sage-light bg-white p-2" data-testid={t.kpiRecordItem(k.key)}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-forest-dark">
                  <Calendar className="h-3 w-3 text-forest-mid" /> <b>{r.date}</b>
                  <span className={`font-heading text-sm font-bold ${scoreColor(r.score)}`}>{r.score}/10</span>
                  {idx === 0 && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">latest → KPI score</span>}
                </div>
                {canEdit && <button onClick={() => del(r.id)} className="shrink-0 text-rose-500 hover:text-rose-700" title="Delete record"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
              {r.note && <p className="mt-0.5 text-[11px] italic text-muted-foreground">“{r.note}”</p>}
              {(r.photos || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {r.photos.map((p) => (
                    <a key={p.id} href={photoUrl(p)} target="_blank" rel="noreferrer">
                      <img src={photoUrl(p)} alt="site check" className="h-12 w-12 rounded border border-sage-light object-cover" />
                    </a>
                  ))}
                </div>
              )}
              {r.created_by_name && <p className="mt-0.5 text-[10px] text-muted-foreground">Logged by {r.created_by_name}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">No site-verification records yet{canEdit ? " — log one below." : "."}</p>
      )}

      {canEdit && (
        <div className="rounded-md border border-dashed border-sage-light bg-sage-surface/40 p-2">
          <div className="flex flex-wrap items-center gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-[150px] text-sm" data-testid={t.kpiRecordDate(k.key)} />
            <Input type="number" min="0" max="10" step="1" value={score} onChange={(e) => setScore(e.target.value)} placeholder="0–10" className="h-8 w-20 text-sm" data-testid={t.kpiRecordScore(k.key)} />
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="On-site observation (optional)" className="h-8 flex-1 min-w-[140px] text-sm" data-testid={t.kpiRecordNote(k.key)} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-sage-light bg-white px-2 py-1 text-[11px] font-medium text-forest-dark hover:bg-sage-light">
              <Camera className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Add photo"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={t.kpiRecordPhoto(k.key)} />
            </label>
            {files.map((p) => (<img key={p.id} src={photoUrl(p)} alt="pending" className="h-8 w-8 rounded border border-sage-light object-cover" />))}
            <Button size="sm" className="h-8 bg-forest-mid text-xs hover:bg-forest-dark" disabled={busy || uploading} onClick={add} data-testid={t.kpiRecordAdd(k.key)}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Log site check
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiRow({ periodId, base, t, k, canEdit, navigate, onChange }) {
  const [score, setScore] = useState(k.override ?? "");
  const [note, setNote] = useState(k.note || "");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [prov, setProv] = useState(null);

  const hasRoots = (k.roots_kpis || []).length > 0;
  const dirty = String(score) !== String(k.override ?? "") || note !== (k.note || "");

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/score/periods/${periodId}/${base}/kpi/${k.key}`, {
        score: score === "" ? null : Number(score), note,
      });
      toast.success("Score updated");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  const signoff = async () => {
    try {
      const r = await api.post(`/score/periods/${periodId}/${base}/kpi/${k.key}/signoff`);
      toast.success(r.data.signed_off ? "KPI signed off" : "Sign-off removed");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleEvidence = async () => {
    const nv = !open;
    setOpen(nv);
    if (nv && prov === null) {
      const entries = await Promise.all((k.roots_kpis || []).map(async (rk) => {
        try { const r = await api.get(`/score/periods/${periodId}/entries/${rk.code}/provenance`); return [rk.code, r.data]; }
        catch { return [rk.code, null]; }
      }));
      setProv(Object.fromEntries(entries));
    }
  };

  const openRecord = (l) => {
    const lk = l.link || {};
    if (lk.area === "setup") navigate(`/setup?tab=${lk.tab}&record=${lk.record_id}`);
    else navigate(`/project?tab=${lk.tab || "work-orders"}`);
  };

  return (
    <div className="rounded-lg border border-sage-light bg-white p-3" data-testid={t.kpi(k.key)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-forest-dark">
            {k.name}
            {k.site_verified && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">Site-verified</span>}
          </p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">Target: {k.target}</p>
        </div>
        <div className="shrink-0 text-right">
          {k.awaiting ? (
            <p className="text-[11px] font-semibold text-amber-600">Awaiting<br />site verification</p>
          ) : (
            <>
              <p className={`font-heading text-2xl font-bold ${scoreColor(k.score)}`}>{k.score == null ? "—" : k.score}<span className="text-xs font-normal text-muted-foreground">/10</span></p>
              {!k.site_verified && <p className="text-[10px] text-muted-foreground">proposed {k.proposed == null ? "—" : k.proposed}</p>}
            </>
          )}
        </div>
      </div>

      {k.site_verified ? (
        <div className="mt-2 space-y-2">
          <SiteRecords periodId={periodId} base={base} t={t} k={k} canEdit={canEdit} onChange={onChange} />
          {canEdit ? (
            <Button size="sm" variant={k.signed_off ? "default" : "outline"}
              className={`h-8 text-xs ${k.signed_off ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={signoff} data-testid={t.kpiSignoff(k.key)}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {k.signed_off ? "Signed" : "Sign off"}
            </Button>
          ) : k.signed_off ? (
            <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Signed</span>
          ) : null}
          {canEdit && k.signed_off && k.signed_by_name && <p className="text-[10px] text-emerald-700">Signed off by {k.signed_by_name}</p>}
        </div>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {canEdit ? (
              <>
                <Input
                  type="number" min="0" max="10" step="1" value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder={k.proposed == null ? "—" : String(k.proposed)}
                  className="h-8 w-20 text-sm" data-testid={t.kpiScore(k.key)}
                />
                <Input
                  value={note} onChange={(e) => setNote(e.target.value)} placeholder="Assessor note (optional)"
                  className="h-8 flex-1 min-w-[140px] text-sm" data-testid={t.kpiNote(k.key)}
                />
                <Button size="sm" className="h-8 bg-forest-mid text-xs hover:bg-forest-dark" disabled={!dirty || busy}
                  onClick={save} data-testid={t.kpiSave(k.key)}>Save</Button>
                <Button size="sm" variant={k.signed_off ? "default" : "outline"}
                  className={`h-8 text-xs ${k.signed_off ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                  onClick={signoff} data-testid={t.kpiSignoff(k.key)}>
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {k.signed_off ? "Signed" : "Sign off"}
                </Button>
              </>
            ) : (
              <>
                {k.override != null && <span className="rounded bg-sage-surface px-1.5 py-0.5 text-[11px] text-forest-dark">assessor set {k.override}/10</span>}
                {k.note && <span className="text-[11px] italic text-muted-foreground">“{k.note}”</span>}
                {k.signed_off && <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Signed</span>}
              </>
            )}
          </div>
          {canEdit && k.signed_off && k.signed_by_name && (
            <p className="mt-1 text-[10px] text-emerald-700">Signed off by {k.signed_by_name}</p>
          )}
        </>
      )}

      {k.source === "gnpr" && k.source_label && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-sage-surface/50 px-2 py-1.5 text-[11px] text-forest-dark">
          <MapPinned className="mt-0.5 h-3 w-3 shrink-0 text-forest-mid" /> Source — LUSH Green Plot Ratio: {k.source_label}
        </p>
      )}
      {k.site_verified && (
        <p className="mt-2 text-[11px] text-muted-foreground">Entered by the assessor from on-site verification — ROOTS does not measure this directly.</p>
      )}

      {hasRoots && (
        <>
          <button onClick={toggleEvidence} className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-forest-mid hover:text-forest-dark"
            data-testid={t.kpiEvidence(k.key)}>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide" : "ROOTS evidence"} ·
            {(k.roots_kpis || []).map((r) => ` ${r.code}${r.score != null ? ` (${r.score})` : ""}`).join(" ")}
          </button>
          {open && (
            <div className="mt-2 space-y-2 border-t border-sage-light pt-2">
              {(k.roots_kpis || []).map((r) => (
                <div key={r.code} className="rounded-md bg-sage-surface/50 p-2">
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-forest-dark">
                    <Database className="h-3 w-3 text-forest-mid" /> <span className="font-mono">{r.code}</span> {r.name}
                    {r.score != null && <span className="text-muted-foreground">· ROOTS score {r.score}/100</span>}
                  </p>
                  <ProvenanceList prov={prov?.[r.code]} onOpenRecord={openRecord} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function JointSignoff({ periodId, base, t, data, canEdit, canReopen, onChange }) {
  const [contractor, setContractor] = useState("");
  const [comments, setComments] = useState("");
  const signed = data.status === "signed";
  const so = data.signoff || {};

  const sign = async () => {
    try {
      await api.post(`/score/periods/${periodId}/${base}/signoff`, { contractor_name: contractor, comments });
      toast.success("Scorecard signed off — now frozen");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reopen = async () => {
    try {
      await api.post(`/score/periods/${periodId}/${base}/reopen`);
      toast("Scorecard reopened to draft");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="rounded-lg border border-sage-light bg-sage-surface p-4">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-forest-dark"><PenLine className="h-4 w-4 text-forest-mid" /> Joint audit sign-off</p>
      {signed ? (
        <div className="mt-2 space-y-1 text-xs text-forest-dark">
          <p className="inline-flex items-center gap-1.5 rounded bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-900"><Lock className="h-3 w-3" /> Signed off &amp; frozen</p>
          <p>Assessor: <b>{so.assessor_name}</b> · {String(so.assessor_at || "").slice(0, 16).replace("T", " ")}</p>
          <p>Contractor representative: <b>{so.contractor_name || "—"}</b></p>
          {so.comments && <p className="italic text-muted-foreground">“{so.comments}”</p>}
          {canReopen && <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" onClick={reopen} data-testid={t.reopen}>Reopen to draft</Button>}
        </div>
      ) : canEdit ? (
        <div className="mt-3 space-y-2">
          <Input value={contractor} onChange={(e) => setContractor(e.target.value)} placeholder="Contractor representative name" className="h-8 text-sm" data-testid={t.contractorName} />
          <Textarea value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Comments / observations (optional)" className="min-h-[60px] text-sm" data-testid={t.comments} />
          <Button size="sm" className="h-8 bg-forest-dark text-xs hover:bg-forest-mid" onClick={sign} data-testid={t.jointSignoff}>
            <CheckCircle2 className="mr-1 h-4 w-4" /> Sign off scorecard
          </Button>
          <p className="text-[11px] text-muted-foreground">Signing off freezes all scores and sign-offs for this assessment period.</p>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">Draft assessment — awaiting assessor sign-off.</p>
      )}
    </div>
  );
}

export default function FrameworkScorecard({ periodId, base, t, readinessLabel, caveat, bonusIcon, pdfName }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const BonusIcon = bonusIcon || Leaf;

  const load = useCallback(async () => {
    if (!periodId) return;
    try {
      const r = await api.get(`/score/periods/${periodId}/${base}`);
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, [periodId, base]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const download = () =>
    downloadFile(`/score/periods/${periodId}/${base}/report.pdf`, pdfName)
      .catch(() => toast.error("PDF export failed"));

  if (loading || !data) return <p className="text-sm text-muted-foreground">Preparing {readinessLabel} scorecard…</p>;

  const sc = data.scorecard;
  const tone = BAND_TONE[sc.band_tone] || BAND_TONE.warn;
  const bonus = sc.bonus;

  return (
    <div className="space-y-4" data-testid={t.panel}>
      {/* Header + readiness band */}
      <div className="rounded-xl border border-sage-light bg-sage-surface p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-0.5 text-xs text-forest-dark">
            <p className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-forest-mid" /> Service provider: <b>{data.header.service_provider}</b></p>
            <p>Property / estate: <b>{data.header.property_estate}</b></p>
            <p>Assessment period: <b>{data.header.period_label}</b></p>
          </div>
          <div className="text-right">
            <div className={`inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 ${tone}`}>
              <BonusIcon className="h-5 w-5" />
              <div className="text-left">
                <p className="font-heading text-2xl font-bold" data-testid={t.readiness}>{sc.readiness == null ? "—" : `${sc.readiness}%`}</p>
                <p className="text-[11px] font-semibold" data-testid={t.band}>{sc.band_label || "—"}</p>
              </div>
            </div>
            <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{readinessLabel}</p>
          </div>
        </div>
        <p className="mt-3 rounded-lg border border-sage-light bg-white p-2.5 text-[11px] text-muted-foreground">
          {caveat}{" "}
          Core score {sc.core_pct == null ? "—" : `${sc.core_pct}%`} + {sc.bonus_title?.toLowerCase()} bonus <b className="text-forest-dark" data-testid={t.bonus}>+{sc.bonus_points}%</b>.
        </p>
      </div>

      {/* Weighted criteria */}
      {sc.criteria.map((crit) => (
        <div key={crit.key} className="rounded-xl border border-sage-light p-3" data-testid={t.criterion(crit.key)}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="font-heading text-sm font-bold text-forest-dark">{crit.title}</p>
            <div className="flex items-center gap-2 text-xs">
              <span className="rounded-full bg-forest-mid px-2 py-0.5 font-semibold text-white">{crit.weight}%</span>
              <span className={`font-heading text-lg font-bold ${scoreColor(crit.score)}`}>{crit.score == null ? "—" : `${crit.score}/10`}</span>
            </div>
          </div>
          <div className="space-y-2">
            {crit.kpis.map((k) => (
              <KpiRow key={k.key} periodId={periodId} base={base} t={t} k={k} canEdit={data.can_edit} navigate={navigate} onChange={load} />
            ))}
          </div>
        </div>
      ))}

      {/* Bonus category */}
      <div className="rounded-xl border border-dashed border-sage-light bg-sage-surface/40 p-3" data-testid={t.criterion("bonus")}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-forest-dark"><BonusIcon className="h-4 w-4 text-forest-mid" /> {sc.bonus_title}</p>
          <span className="rounded-full bg-sage-accent px-2 py-0.5 text-xs font-semibold text-white">+{sc.bonus_max}% bonus · adds {sc.bonus_points}%</span>
        </div>
        <KpiRow periodId={periodId} base={base} t={t} k={bonus} canEdit={data.can_edit} navigate={navigate} onChange={load} />
      </div>

      <JointSignoff periodId={periodId} base={base} t={t} data={data} canEdit={data.can_edit} canReopen={data.can_reopen} onChange={load} />

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-sage-light pt-3">
        <p className="text-[11px] text-muted-foreground">
          Mappable scores are auto-derived from the issued ROOTS evidence; <HardHat className="inline h-3 w-3" /> site-verified items are entered by the assessor. A readiness aid — not an NParks certification.
        </p>
        <Button size="sm" variant="outline" onClick={download} data-testid={t.downloadPdf}><Download className="mr-1 h-4 w-4" /> Scorecard PDF</Button>
      </div>
    </div>
  );
}
